import { smIconSize } from '@/constants';
import { useInstrumentRunSamplesCsvDownload } from '@/hooks';
import { useProteoformResultsTableConfig } from '@/hooks/useAssays';
import type { InstrumentRunPublic, SampleProteoformPublic } from '@/types';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { Download } from 'lucide-react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from '../../styles.module.css';

import { buildRunSampleDetailsColDefs } from './colDefs';

interface Props {
  orgId: string;
  run: InstrumentRunPublic;
}

// TODO: switch to rowModelType="serverSide" with createAgGridDatasource once
//  orgs/${orgId}/instrument_runs/${runId}/sample_proteoforms/search ships.
//  Use instrumentRunQueryKeys.endpoints.proteoformsSearch(orgId, run.id) for the URL.

const PLACEHOLDER = '—';

const RunSampleDetailsSection = ({ orgId, run }: Props) => {
  const { t } = useTranslation('runs', { keyPrefix: 'sampleDetails' });
  const downloadSamples = useInstrumentRunSamplesCsvDownload(orgId, run.id);

  // POR-308 (narrow slice): the isoform/PTM columns + threshold come from the assay's
  // proteoform results table config (POR-322) instead of a hardcoded Tau-only column set.
  // The run now carries a real `assay_id`, so this is a direct lookup.
  const { data: tableConfig } = useProteoformResultsTableConfig(run.assay_id);

  // i18next's strongly-typed t only accepts the known key union; bridge to the
  // plain (key: string) => string signature that colDefs expects.
  const colDefs = useMemo(() => buildRunSampleDetailsColDefs(key => t(key as never), tableConfig), [t, tableConfig]);

  return (
    <Stack className={styles.sampleDetailsContainer} gap={3} orientation="vertical">
      {/* Title row */}
      <Stack className={styles.sampleDetailsTitleRow} orientation="horizontal">
        <Text as="h3">{t('title')}</Text>

        <Stack className={styles.statsRow} gap={2} orientation="horizontal">
          <Button
            data-testid="run-sample-details-download"
            isPending={downloadSamples.isPending}
            onPress={() => {
              downloadSamples.mutate(undefined, {
                onError: () => toast.error(t('download_failed')),
              });
            }}
            visual="unstyled"
          >
            <Download size={smIconSize} />
            {t('download')}
          </Button>

          {/* TODO: Proteoforms total from pagination.total once run-scoped endpoint ships */}
          <Stack className={styles.statPill} gap={1} orientation="horizontal">
            <Text size="xs">{t('stat_proteoforms')}</Text>
            <Text size="xs">{PLACEHOLDER}</Text>
          </Stack>

          {/* TODO: PTMs count — no API backing yet */}
          <Stack className={styles.statPill} gap={1} orientation="horizontal">
            <Text size="xs">{t('stat_ptms')}</Text>
            <Text size="xs">{PLACEHOLDER}</Text>
          </Stack>

          {/* TODO: Isoforms count — no API backing yet */}
          <Stack className={styles.statPill} gap={1} orientation="horizontal">
            <Text size="xs">{t('stat_isoforms')}</Text>
            <Text size="xs">{PLACEHOLDER}</Text>
          </Stack>

          {/* Threshold — now sourced from the assay's proteoform results table config (POR-322).
              Formatted with toLocaleString()/the `{{value}} cpm` i18n key, matching the
              abundance column (colDefs.ts) and the sample-detail threshold stat
              (ProteoformAbundances.tsx) for consistent unit + thousands-separator display. */}
          <Stack className={styles.statPill} gap={1} orientation="horizontal">
            <Text size="xs">{t('stat_threshold')}</Text>
            <Text size="xs">
              {tableConfig?.default_threshold_cpm !== null && tableConfig?.default_threshold_cpm !== undefined
                ? t('stat_threshold_value', { value: tableConfig.default_threshold_cpm.toLocaleString() })
                : PLACEHOLDER}
            </Text>
          </Stack>
        </Stack>
      </Stack>

      {/* Placeholder grid — rowData={[]} until run-scoped proteoform endpoint exists */}
      <Table
        columnDefs={colDefs}
        defaultColDef={{ resizable: true }}
        enableAdvancedFilter={false}
        enableResponsiveResize={false}
        pagination
        paginationPageSize={25}
        paginationPageSizeSelector={[25, 50, 100]}
        rowData={[] as SampleProteoformPublic[]}
        tableId={`run-sample-details-${run.id}`}
      />
    </Stack>
  );
};

export default RunSampleDetailsSection;
